<template>
  <div class="page-container">
    <!-- 筛选区域：保持原样 -->
    <Filter :params="params" @search="searchData">
      <template #handle>
        <el-form-item>
          <el-button type="danger" @click="handleAlarm">
            <span class="alarm-icon">!</span>
            告警
          </el-button>
        </el-form-item>
      </template>
    </Filter>

    <!-- 表格区域 -->
    <Table
      ref="tableRef"
      :tableData="tableDataList"
      :total="data.total"
      @update="updatePage"
      @sort-change="handleSortChange"
    >
      <!-- 名称（两行：主名称 + 副编码） -->
      <el-table-column label="名称" min-width="180">
        <template #default="{ row }">
          <div class="name-cell">
            <div class="name-main">{{ row.name }}</div>
            <div class="name-sub">{{ row.code }}</div>
          </div>
        </template>
      </el-table-column>

      <!-- 形态 -->
      <el-table-column prop="form" label="形态" min-width="140" />

      <!-- 状态 -->
      <el-table-column prop="status" label="状态" width="120">
        <template #default="{ row }">
          <span :class="getStatusClass(row.status)">{{ row.status }}</span>
        </template>
      </el-table-column>

      <!-- 主电池 SOC -->
      <el-table-column prop="soc" label="主电池 SOC" width="120" />

      <!-- 异常部件 -->
      <el-table-column prop="abnormal" label="异常部件" width="100">
        <template #default="{ row }">
          <span :class="{ 'abnormal-red': row.abnormal > 0 }">
            {{ row.abnormal }}
          </span>
        </template>
      </el-table-column>

      <!-- 最后心跳 -->
      <el-table-column prop="lastHeartbeat" label="最后心跳" width="140" />

      <!-- 操作 -->
      <el-table-column label="操作" fixed="right" width="120">
        <template #default="{ row }">
          <el-button class="view-btn" size="small" @click="handleView(row)">查看</el-button>
        </template>
      </el-table-column>
    </Table>

    <!-- ===== 告警中心抽屉 ===== -->
    <el-drawer
      v-model="data.alarmVisible"
      title="告警中心"
      direction="rtl"
      size="520px"
      :with-header="true"
      class="alarm-drawer"
    >
      <div class="alarm-list">
        <div
          v-for="(item, index) in alarmList"
          :key="index"
          class="alarm-item"
        >
          <!-- 第一行：红点 + 名称 + 故障标签 -->
          <div class="alarm-header">
            <span class="alarm-dot"></span>
            <span class="alarm-name">{{ item.name }}</span>
            <span class="alarm-tag">故障</span>
          </div>

          <!-- 第二行：描述 -->
          <div class="alarm-desc">{{ item.desc }}</div>

          <!-- 第三行：代码 / 部件 / 时间 -->
          <div class="alarm-meta">
            <span>代码 {{ item.code }}</span>
            <span class="divider">|</span>
            <span>{{ item.part }}</span>
            <span class="divider">|</span>
            <span>{{ item.time }}</span>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="alarmList.length === 0" class="alarm-empty">
          暂无告警
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import Table from "@/components/table/index";
import Filter from "@/components/table/filter";
import { ref, reactive } from "vue";

// ===== 形态下拉数据源 =====
const formList = [
  { translatedValue: '全部形态', code: '' },
  { translatedValue: '轮式 Kargo', code: '轮式 Kargo' },
  { translatedValue: '人形 小人型', code: '人形 小人型' },
  { translatedValue: '双臂 K1', code: '双臂 K1' },
];

export default {
  name: 'EmbodiedMonitoring',
  components: {
    Table,
    Filter,
  },
  setup() {
    // ===== 筛选参数：形态下拉 =====
    const params = [
      {
        type: 'Select',
        key: 'form',
        value: '',
        label: '形态',
        options: {
          data: formList,
          label: 'translatedValue',
          value: 'code'
        }
      },
    ];

    // ===== 数据状态 =====
    const data = reactive({
      page: 1,
      size: 10,
      total: 0,
      alarmVisible: false,
    });

    const tableDataList = ref([]);
    const query = reactive({ form: '' });

    // ===== Mock 数据（设备列表） =====
    const mockData = [
      { id: 1, name: 'Kargo-01',  code: 'JkPi_K01',   form: '轮式 Kargo',  status: '故障',      soc: '78%', abnormal: 1, lastHeartbeat: '3s前' },
      { id: 2, name: 'Kargo-02',  code: 'JkPi_K02',   form: '轮式 Kargo',  status: '在线/正常', soc: '91%', abnormal: 0, lastHeartbeat: '2s前' },
      { id: 3, name: '小人型-01', code: 'JkPi_H01',   form: '人形 小人型', status: '在线/正常', soc: '92%', abnormal: 0, lastHeartbeat: '1s前' },
      { id: 4, name: '小人型-02', code: 'JkPi_H02',   form: '人形 小人型', status: '故障',      soc: '64%', abnormal: 1, lastHeartbeat: '1s前' },
      { id: 5, name: 'Kargo-03',  code: 'JkPi_K03',   form: '轮式 Kargo',  status: '离线',      soc: '0%',  abnormal: 0, lastHeartbeat: '超时 28s' },
      { id: 6, name: 'K1',        code: 'JkPi_K1',    form: '双臂 K1',     status: '在线/正常', soc: '—',   abnormal: 0, lastHeartbeat: '2s前' },
      { id: 7, name: 'K1-02',     code: 'JkPi_K1_02', form: '双臂 K1',     status: '故障',      soc: '—',   abnormal: 2, lastHeartbeat: '5s前' },
    ];

    // ===== Mock 数据（告警列表，与截图一致） =====
    const alarmList = ref([
      {
        name: 'Kargo-03',
        desc: '离线：心跳丢失，置为离线',
        code: '—',
        part: '离线',
        time: '超时 28s',
      },
      {
        name: 'K1-02',
        desc: '右夹爪报错，已锁定',
        code: '0x01',
        part: 'gripper',
        time: '19:50:02',
      },
      {
        name: 'K1-02',
        desc: 'J4 编码器故障，右臂禁用',
        code: '0x10',
        part: 'right_arm',
        time: '19:50:02',
      },
      {
        name: 'Kargo-01',
        desc: '[左夹爪] 堵转报错',
        code: '0xE1',
        part: 'gripper',
        time: '19:42:01',
      },
      {
        name: '小人型-02',
        desc: '[R_Hip] EtherCAT 从站丢失 OP',
        code: '0x1002',
        part: 'drive',
        time: '19:41:10',
      },
      {
        name: '小人型-02',
        desc: '[R_Knee] 同上',
        code: '0x1002',
        part: 'drive',
        time: '19:41:09',
      },
    ]);

    // ===== 状态颜色 =====
    const getStatusClass = (status) => {
      if (status === '故障') return 'status-red';
      if (status === '在线/正常') return 'status-green';
      if (status === '离线') return 'status-gray';
      return '';
    };

    // ===== 获取数据（Mock 前端过滤 + 分页） =====
    const getData = async () => {
      let filtered = mockData.filter(item => {
        const matchForm = query.form ? item.form === query.form : true;
        return matchForm;
      });

      data.total = filtered.length;
      const start = (data.page - 1) * data.size;
      const end = start + data.size;
      tableDataList.value = filtered.slice(start, end);
    };

    // ===== 搜索 / 分页 / 排序 =====
    const searchData = (searchParams) => {
      Object.assign(query, searchParams);
      data.page = 1;
      getData();
    };

    const updatePage = (page, size) => {
      data.page = page || 1;
      data.size = size || 10;
      getData();
    };

    const handleSortChange = () => getData();

    // ===== 查看 =====
    const handleView = (row) => {
      ElMessage.info(`查看设备：${row.name}（${row.code}）`);
    };

    // ===== 告警：打开抽屉 =====
    const handleAlarm = () => {
      data.alarmVisible = true;
    };

    // ===== 初始化 =====
    getData();

    return {
      params,
      data,
      tableDataList,
      alarmList,
      getStatusClass,
      searchData,
      updatePage,
      handleSortChange,
      handleView,
      handleAlarm,
    };
  },
};
</script>

<style lang="scss" scoped>
.page-container {
  height: 100%;
  padding: 50px 10px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

/* 告警图标：红底白字感叹号 */
.alarm-icon {
  display: inline-block;
  width: 14px;
  height: 14px;
  line-height: 14px;
  text-align: center;
  border-radius: 50%;
  background: #f56c6c;
  color: #fff;
  font-size: 12px;
  font-weight: bold;
  margin-right: 6px;
}

/* 名称两行 */
.name-cell { line-height: 1.4; }
.name-main { font-weight: 600; color: #303133; }
.name-sub  { font-size: 12px; color: #909399; }

/* 状态颜色 */
.status-red   { color: #f56c6c; }
.status-green { color: #67c23a; }
.status-gray  { color: #909399; }

/* 异常部件红色 */
.abnormal-red { color: #f56c6c; font-weight: 600; }

/* 查看按钮：红色边框、白底红字 */
.view-btn {
  color: #f56c6c;
  border-color: #f56c6c;
  background: #fff;
}
.view-btn:hover {
  background: #fef0f0;
  border-color: #f56c6c;
  color: #f56c6c;
}

/* ===== 告警抽屉样式 ===== */
/* 标题左对齐 */
.alarm-drawer :deep(.el-drawer__header) {
  margin-bottom: 0;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  text-align: left;
}

.alarm-drawer :deep(.el-drawer__title) {
  text-align: left;
  font-weight: 600;
  color: #303133;
}

/* 抽屉内容左对齐 */
.alarm-drawer :deep(.el-drawer__body) {
  padding: 0 20px;
  text-align: left;
}

.alarm-list {
  padding: 20px;
}

.alarm-item {
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
  text-align: left;
}

.alarm-item:last-child {
  border-bottom: none;
}

/* 第一行：红点 + 名称 + 故障标签 */
.alarm-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 8px;
}

.alarm-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f56c6c;
  margin-right: 8px;
  flex-shrink: 0;
}

.alarm-name {
  font-weight: 600;
  color: #303133;
  margin-right: 10px;
}

.alarm-tag {
  display: inline-block;
  font-size: 12px;
  color: #f56c6c;
  background: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 3px;
  padding: 0 6px;
  line-height: 18px;
}

/* 第二行：描述 */
.alarm-desc {
  font-size: 13px;
  color: #303133;
  margin-left: 16px;
  margin-bottom: 4px;
  text-align: left;
}

/* 第三行：meta */
.alarm-meta {
  font-size: 12px;
  color: #909399;
  margin-left: 16px;
  text-align: left;
}

.alarm-meta .divider {
  margin: 0 6px;
  color: #dcdfe6;
}

/* 空状态 */
.alarm-empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 40px 0;
}
</style>